"use client";

import { c as _c } from "react/compiler-runtime";
import { Combobox as ComboboxPrimitive } from "@base-ui/react";
import { ArrowDown01Icon, Cancel01Icon, Tick02Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/components/ui/input-group";
import { cn } from "@/lib/utils";
type ComboboxUiState = {
  showClearButton: boolean;
};
const ComboboxUiContext = React.createContext<ComboboxUiState | null>(null);
function hasComboboxValue(value: unknown) {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return value !== null && value !== undefined && value !== "";
}
function Combobox(props) {
  const $ = _c(29);
  let defaultValue;
  let onInputValueChange;
  let onValueChange;
  let rest;
  let value;
  if ($[0] !== props) {
    ({
      value,
      defaultValue,
      onValueChange,
      onInputValueChange,
      ...rest
    } = props);
    $[0] = props;
    $[1] = defaultValue;
    $[2] = onInputValueChange;
    $[3] = onValueChange;
    $[4] = rest;
    $[5] = value;
  } else {
    defaultValue = $[1];
    onInputValueChange = $[2];
    onValueChange = $[3];
    rest = $[4];
    value = $[5];
  }
  const isControlled = value !== undefined;
  const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue);
  const [inputValue, setInputValue] = React.useState("");
  const currentValue = isControlled ? value : uncontrolledValue;
  let t0;
  if ($[6] !== currentValue || $[7] !== inputValue) {
    t0 = hasComboboxValue(currentValue) || inputValue.length > 0;
    $[6] = currentValue;
    $[7] = inputValue;
    $[8] = t0;
  } else {
    t0 = $[8];
  }
  const showClearButton = t0;
  let t1;
  let t2;
  if ($[9] !== isControlled || $[10] !== value) {
    t1 = () => {
      if (isControlled && !hasComboboxValue(value)) {
        setInputValue("");
      }
    };
    t2 = [isControlled, value];
    $[9] = isControlled;
    $[10] = value;
    $[11] = t1;
    $[12] = t2;
  } else {
    t1 = $[11];
    t2 = $[12];
  }
  React.useEffect(t1, t2);
  let t3;
  if ($[13] !== showClearButton) {
    t3 = {
      showClearButton
    };
    $[13] = showClearButton;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== isControlled || $[16] !== onValueChange) {
    t4 = (nextValue, eventDetails) => {
      if (!isControlled) {
        setUncontrolledValue(nextValue as ComboboxPrimitive.Root.Props<Value, Multiple>["value"]);
      }
      if (!hasComboboxValue(nextValue)) {
        setInputValue("");
      }
      onValueChange?.(nextValue, eventDetails);
    };
    $[15] = isControlled;
    $[16] = onValueChange;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== onInputValueChange) {
    t5 = (nextInputValue, eventDetails_0) => {
      setInputValue(nextInputValue);
      onInputValueChange?.(nextInputValue, eventDetails_0);
    };
    $[18] = onInputValueChange;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== defaultValue || $[21] !== rest || $[22] !== t4 || $[23] !== t5 || $[24] !== value) {
    t6 = <ComboboxPrimitive.Root {...rest} defaultValue={defaultValue} value={value} onValueChange={t4} onInputValueChange={t5} />;
    $[20] = defaultValue;
    $[21] = rest;
    $[22] = t4;
    $[23] = t5;
    $[24] = value;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== t3 || $[27] !== t6) {
    t7 = <ComboboxUiContext.Provider value={t3}>{t6}</ComboboxUiContext.Provider>;
    $[26] = t3;
    $[27] = t6;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  return t7;
}
function ComboboxValue(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function ComboboxTrigger(t0) {
  const $ = _c(13);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      children,
      "aria-label": t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const ariaLabel = t1 === undefined ? "Open options" : t1;
  let t2;
  if ($[5] !== className) {
    t2 = cn("[&_svg:not([class*='size-'])]:size-4", className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <HugeiconsIcon icon={ArrowDown01Icon} strokeWidth={2} className="pointer-events-none size-4 text-muted-foreground" />;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== ariaLabel || $[9] !== children || $[10] !== props || $[11] !== t2) {
    t4 = <ComboboxPrimitive.Trigger data-slot="combobox-trigger" className={t2} aria-label={ariaLabel} {...props}>{children}{t3}</ComboboxPrimitive.Trigger>;
    $[8] = ariaLabel;
    $[9] = children;
    $[10] = props;
    $[11] = t2;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function ComboboxClear(t0) {
  const $ = _c(12);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      "aria-label": t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const ariaLabel = t1 === undefined ? "Clear selection" : t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <InputGroupButton variant="ghost" size="icon-xs" />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== className) {
    t3 = cn(className);
    $[5] = className;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <HugeiconsIcon icon={Cancel01Icon} strokeWidth={2} className="pointer-events-none" />;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== ariaLabel || $[9] !== props || $[10] !== t3) {
    t5 = <ComboboxPrimitive.Clear data-slot="combobox-clear" render={t2} className={t3} aria-label={ariaLabel} {...props}>{t4}</ComboboxPrimitive.Clear>;
    $[8] = ariaLabel;
    $[9] = props;
    $[10] = t3;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function ComboboxInput(t0) {
  const $ = _c(28);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      className,
      children,
      disabled: t1,
      showTrigger: t2,
      showClear: t3,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
  }
  const disabled = t1 === undefined ? false : t1;
  const showTrigger = t2 === undefined ? true : t2;
  const showClear = t3 === undefined ? false : t3;
  const comboboxUiState = React.useContext(ComboboxUiContext);
  const shouldShowClear = showClear && comboboxUiState?.showClearButton === true;
  const shouldShowTrigger = showTrigger && !shouldShowClear;
  let t4;
  if ($[7] !== className) {
    t4 = cn("w-auto", className);
    $[7] = className;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== disabled) {
    t5 = <InputGroupInput disabled={disabled} />;
    $[9] = disabled;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== props || $[12] !== t5) {
    t6 = <ComboboxPrimitive.Input render={t5} {...props} />;
    $[11] = props;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== disabled || $[15] !== shouldShowTrigger) {
    t7 = shouldShowTrigger && <InputGroupButton size="icon-xs" variant="ghost" asChild={true} data-slot="input-group-button" className="data-pressed:bg-transparent" disabled={disabled}><ComboboxTrigger /></InputGroupButton>;
    $[14] = disabled;
    $[15] = shouldShowTrigger;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== disabled || $[18] !== shouldShowClear) {
    t8 = shouldShowClear && <ComboboxClear disabled={disabled} />;
    $[17] = disabled;
    $[18] = shouldShowClear;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t7 || $[21] !== t8) {
    t9 = <InputGroupAddon align="inline-end">{t7}{t8}</InputGroupAddon>;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== children || $[24] !== t4 || $[25] !== t6 || $[26] !== t9) {
    t10 = <InputGroup className={t4}>{t6}{t9}{children}</InputGroup>;
    $[23] = children;
    $[24] = t4;
    $[25] = t6;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}
function ComboboxContent(t0) {
  const $ = _c(21);
  let anchor;
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      className,
      side: t1,
      sideOffset: t2,
      align: t3,
      alignOffset: t4,
      anchor,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = anchor;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    anchor = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
  }
  const side = t1 === undefined ? "bottom" : t1;
  const sideOffset = t2 === undefined ? 6 : t2;
  const align = t3 === undefined ? "start" : t3;
  const alignOffset = t4 === undefined ? 0 : t4;
  const t5 = !!anchor;
  let t6;
  if ($[8] !== className) {
    t6 = cn("group/combobox-content data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:fade-in-0 data-open:zoom-in-95 data-closed:fade-out-0 data-closed:zoom-out-95 relative max-h-(--available-height) w-(--anchor-width) min-w-(--anchor-width) max-w-(--available-width) origin-(--transform-origin) overflow-hidden rounded-3xl bg-popover text-popover-foreground shadow-lg ring-1 ring-foreground/5 duration-100 data-closed:animate-out data-open:animate-in *:data-[slot=input-group]:m-1.5 *:data-[slot=input-group]:mb-0 *:data-[slot=input-group]:h-8 *:data-[slot=input-group]:border-input/30 *:data-[slot=input-group]:bg-input/50 *:data-[slot=input-group]:shadow-none dark:ring-foreground/10", className);
    $[8] = className;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== props || $[11] !== t5 || $[12] !== t6) {
    t7 = <ComboboxPrimitive.Popup data-slot="combobox-content" data-chips={t5} className={t6} {...props} />;
    $[10] = props;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== align || $[15] !== alignOffset || $[16] !== anchor || $[17] !== side || $[18] !== sideOffset || $[19] !== t7) {
    t8 = <ComboboxPrimitive.Portal><ComboboxPrimitive.Positioner side={side} sideOffset={sideOffset} align={align} alignOffset={alignOffset} anchor={anchor} className="isolate z-50">{t7}</ComboboxPrimitive.Positioner></ComboboxPrimitive.Portal>;
    $[14] = align;
    $[15] = alignOffset;
    $[16] = anchor;
    $[17] = side;
    $[18] = sideOffset;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
function ComboboxList(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("no-scrollbar max-h-[min(calc(--spacing(72)---spacing(9)),calc(var(--available-height)---spacing(9)))] scroll-py-1.5 overflow-y-auto overscroll-contain p-1.5 data-empty:p-0", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.List data-slot="combobox-list" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function ComboboxItem(t0) {
  const $ = _c(11);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("relative flex w-full cursor-default select-none items-center gap-2.5 rounded-2xl py-2 pr-8 pl-3 font-medium text-sm outline-hidden data-disabled:pointer-events-none data-highlighted:bg-accent data-highlighted:text-accent-foreground data-disabled:opacity-50 not-data-[variant=destructive]:data-highlighted:**:text-accent-foreground [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ComboboxPrimitive.ItemIndicator render={<span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center" />}><HugeiconsIcon icon={Tick02Icon} strokeWidth={2} className="pointer-events-none" /></ComboboxPrimitive.ItemIndicator>;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== props || $[9] !== t1) {
    t3 = <ComboboxPrimitive.Item data-slot="combobox-item" className={t1} {...props}>{children}{t2}</ComboboxPrimitive.Item>;
    $[7] = children;
    $[8] = props;
    $[9] = t1;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
function ComboboxGroup(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.Group data-slot="combobox-group" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function ComboboxLabel(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("px-3 py-2.5 text-muted-foreground text-xs", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.GroupLabel data-slot="combobox-label" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function ComboboxCollection(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function ComboboxEmpty(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("hidden w-full justify-center py-2 text-center text-muted-foreground text-sm group-data-empty/combobox-content:flex", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.Empty data-slot="combobox-empty" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function ComboboxSeparator(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("-mx-1.5 my-1.5 h-px bg-border", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.Separator data-slot="combobox-separator" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function ComboboxChips(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex min-h-9 flex-wrap items-center gap-1.5 rounded-3xl border border-input bg-background bg-clip-padding px-3 py-1.5 text-sm transition-[color,box-shadow,background-color] focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/30 has-aria-invalid:border-destructive has-data-[slot=combobox-chip]:px-1.5 has-aria-invalid:ring-3 has-aria-invalid:ring-destructive/20 dark:border-transparent dark:bg-input/50 dark:has-aria-invalid:border-destructive/50 dark:has-aria-invalid:ring-destructive/40", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.Chips data-slot="combobox-chips" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function ComboboxChip(t0) {
  const $ = _c(14);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      children,
      showRemove: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const showRemove = t1 === undefined ? true : t1;
  let t2;
  if ($[5] !== className) {
    t2 = cn("flex w-fit items-center justify-center gap-1 whitespace-nowrap rounded-3xl bg-input px-2 font-medium text-foreground text-xs has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-data-[slot=combobox-chip-remove]:pr-0 has-disabled:opacity-50 dark:bg-input/60", className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== showRemove) {
    t3 = showRemove && <ComboboxPrimitive.ChipRemove render={<Button variant="ghost" size="icon-xs" />} className="mr-1 -ml-1 opacity-50 hover:opacity-100" data-slot="combobox-chip-remove"><HugeiconsIcon icon={Cancel01Icon} strokeWidth={2} className="pointer-events-none" /></ComboboxPrimitive.ChipRemove>;
    $[7] = showRemove;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== children || $[10] !== props || $[11] !== t2 || $[12] !== t3) {
    t4 = <ComboboxPrimitive.Chip data-slot="combobox-chip" className={t2} {...props}>{children}{t3}</ComboboxPrimitive.Chip>;
    $[9] = children;
    $[10] = props;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
function ComboboxChipsInput(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("min-w-16 flex-1 outline-none", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.Input data-slot="combobox-chip-input" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function useComboboxAnchor() {
  return React.useRef(null);
}
export { Combobox, ComboboxInput, ComboboxContent, ComboboxList, ComboboxItem, ComboboxGroup, ComboboxLabel, ComboboxCollection, ComboboxEmpty, ComboboxSeparator, ComboboxChips, ComboboxChip, ComboboxChipsInput, ComboboxTrigger, ComboboxValue, useComboboxAnchor };